import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Layout";
import { PageHeader, Overline } from "@/components/Bits";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, Lightbulb, Sparkles, ArrowLeft, ShieldAlert } from "lucide-react";

export default function Conversation() {
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));
  const endRef = useRef(null);

  useEffect(() => { api.get("/conversation/scenarios").then(({ data }) => setScenarios(data)); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const start = (s) => { setActive(s); setMessages([{ role: "system", text: s.situation }]); };

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: userMsg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/conversation/message", { scenario_slug: active.slug, session_id: sessionId, message: userMsg, level: active.level });
      setMessages((m) => [...m, { role: "assistant", ...data }]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setMessages((m) => [...m, { role: "error", text: formatApiError(err.response?.data?.detail) }]);
    } finally { setLoading(false); }
  };

  if (!active) {
    return (
      <>
        <Seo title="Conversaciones con IA" description="Practica conversaciones reales en alemán con un simulador que corrige tus errores y explica en español." noindex />
        <PageHeader overline="Conversaciones con IA" title="Practica situaciones reales sin miedo a equivocarte"
          subtitle="La IA interpreta un personaje, adapta la dificultad a tu nivel y corrige tus mensajes explicándote en español. No ofrece asesoramiento legal, médico, fiscal ni migratorio." />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {scenarios.map((s) => (
              <button key={s.slug} onClick={() => start(s)} data-testid={`scenario-${s.slug}`}
                className="text-left rounded-lg border border-border bg-card p-6 hover:-translate-y-1 hover:border-primary/40 transition-transform">
                <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">{s.level}</span>
                <h2 className="font-display font-semibold text-lg mt-3">{s.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{s.situation}</p>
              </button>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo title={active.title} noindex />
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 flex flex-col min-h-[80vh]">
        <button onClick={() => setActive(null)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4" data-testid="conv-back"><ArrowLeft size={14} />Otros escenarios</button>
        <div className="rounded-lg border border-border bg-card p-5 mb-4">
          <Overline>Escenario · {active.level}</Overline>
          <h1 className="font-display font-bold text-xl">{active.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">Personaje: {active.character}</p>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto pb-4" data-testid="conv-messages">
          {messages.map((m, i) => {
            if (m.role === "system") return <div key={i} className="text-center text-xs text-muted-foreground bg-secondary rounded-md p-3 flex items-center justify-center gap-2"><Sparkles size={12} />{m.text}</div>;
            if (m.role === "user") return <div key={i} className="flex justify-end"><div className="max-w-[80%] rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm">{m.text}</div></div>;
            if (m.role === "error") return <div key={i} className="rounded-md bg-destructive/10 text-destructive p-3 text-sm flex items-center gap-2"><ShieldAlert size={14} />{m.text}</div>;
            return (
              <div key={i} className="space-y-2">
                <div className="flex justify-start"><div className="max-w-[80%] rounded-lg bg-secondary px-4 py-2.5"><p className="text-sm font-medium">{m.reply_de}</p>{m.reply_es && <p className="text-xs text-muted-foreground mt-1">{m.reply_es}</p>}</div></div>
                {m.correction && m.correction.toLowerCase() !== "sin errores importantes" && (
                  <div className="rounded-md bg-amber-500/10 p-3 text-sm"><p className="font-semibold flex items-center gap-1 text-amber-700 dark:text-amber-400"><Lightbulb size={13} />Corrección</p><p className="text-muted-foreground mt-1">{m.correction}</p>{m.better_version && <p className="mt-1"><span className="text-muted-foreground">Más natural: </span><strong>{m.better_version}</strong></p>}</div>
                )}
              </div>
            );
          })}
          {loading && <div className="flex justify-start"><div className="rounded-lg bg-secondary px-4 py-3"><Loader2 className="animate-spin text-muted-foreground" size={16} /></div></div>}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="sticky bottom-0 bg-background pt-3 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Escribe en alemán..." data-testid="conv-input"
            className="flex-1 h-12 rounded-md border border-input bg-background px-4 text-sm" />
          <Button type="submit" disabled={loading || !input.trim()} data-testid="conv-send" className="h-12 w-12 p-0 rounded-md">{loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}</Button>
        </form>
        <p className="text-xs text-muted-foreground mt-2">Plan gratuito: hasta 20 mensajes al día. La IA solo guarda los errores necesarios para tu aprendizaje.</p>
      </div>
    </>
  );
}
